import Header from './components/Header';
import CustomerQuoteForm from './components/CustomerQuoteForm';
import BusinessRegistrationForm from './components/BusinessRegistrationForm';
import useReveal from './hooks/useReveal';
import './App.css';

function App() {
  const revealRef = useReveal();

  const logo = `${process.env.PUBLIC_URL}/assets/logo/favicon-orange.svg`;

  return (
    <div ref={revealRef}>
      <Header />

      <main id="top">
        {/* Hero */}
        <section className="hero section section-observe" data-section="top">
          <div className="hero-grid">
            <div className="hero-brand reveal" aria-label="Quoteramaa brand mark">
              <div className="hero-logo-lockup">
                <img src={logo} alt="" className="hero-mark" />
                <h1 className="wordmark">Quoteramaa</h1>
              </div>
              <p className="origin">Registered in Alberta · Built for Canada</p>
            </div>

            <div className="hero-copy reveal delay-1">
              <p className="eyebrow">The smarter way to quote</p>
              <h2>One request. Local options. Clear decisions.</h2>
              <p className="lead">Compare service quotes without calling around, repeating details, or chasing replies.</p>
              <div className="hero-actions">
                <a className="btn primary" href="#quote">Get local quotes</a>
                <a className="btn secondary" href="#register">Join as a business</a>
              </div>
              <div className="hero-proof" aria-label="Quoteramaa benefits">
                <span>Organized requests</span>
                <span>Follow-up questions</span>
                <span>Side-by-side quotes</span>
              </div>
            </div>
          </div>
          <a href="#how" className="scroll-cue"><span></span>Scroll</a>
        </section>

        {/* How it works */}
        <section id="how" className="section intro section-observe" data-section="how">
          <div className="section-kicker">How it works</div>
          <div className="split-heading reveal">
            <h2>Less chasing. More choosing.</h2>
            <p>Submit once, clarify once, compare everything in one clean flow.</p>
          </div>
          <div className="steps">
            <article className="card reveal"><span>01</span><h3>Send one request</h3><p>Share the service, timing, location, and key details.</p></article>
            <article className="card reveal delay-1"><span>02</span><h3>Answer follow-ups</h3><p>Businesses ask only what they need before quoting.</p></article>
            <article className="card reveal delay-2"><span>03</span><h3>Compare clearly</h3><p>Review price, scope, availability, and next steps together.</p></article>
          </div>
        </section>

        {/* Categories */}
        <section id="categories" className="section categories section-observe" data-section="categories">
          <div className="section-kicker">Launch categories</div>
          <div className="split-heading reveal">
            <h2>Everyday services. Better quote flow.</h2>
            <p>Focused on high-intent local needs where clear details matter.</p>
          </div>
          <div className="category-grid reveal delay-1">
            <div>Movers</div><div>Move-out cleaning</div><div>Handyman</div>
            <div>Furniture assembly</div><div>Painting</div><div>Internet setup</div>
            <div>Mobile plans</div><div>Tenant insurance</div><div>Auto insurance partners</div>
          </div>
        </section>

        {/* For customers / For businesses */}
        <section id="customers" className="section two-panel section-observe" data-section="businesses">
          <div className="panel large reveal">
            <p className="eyebrow">For customers</p>
            <h2>Quotes without the messy inbox.</h2>
            <p>Keep requests organized, compare realistic options, and choose the provider that fits your budget and timeline.</p>
          </div>
          <div id="businesses" className="panel large reveal delay-1">
            <p className="eyebrow">For businesses</p>
            <h2>Clearer leads. Better conversations.</h2>
            <p>Receive structured quote requests, ask follow-up questions, and respond when the job fits your service area and schedule.</p>
          </div>
        </section>

        {/* Feature strip */}
        <section className="section feature-strip">
          <div className="feature-card reveal"><span></span><p>Customer details stay organized before the conversation moves forward.</p></div>
          <div className="feature-card reveal delay-1"><span></span><p>Businesses can request missing information before giving final numbers.</p></div>
          <div className="feature-card reveal delay-2"><span></span><p>Insurance requests are routed carefully to licensed professionals only.</p></div>
        </section>

        {/* About */}
        <section id="about" className="section about-section section-observe" data-section="about">
          <div className="section-kicker">About us</div>
          <div className="split-heading reveal">
            <h2>Built to make local quoting clearer.</h2>
            <p>Quoteramaa helps customers submit one organized request and compare local service options with less back-and-forth.</p>
          </div>
        </section>

        {/* Business registration */}
        <section id="register" className="section register-section section-observe" data-section="register">
          <div className="register-copy reveal">
            <p className="eyebrow">Business registration</p>
            <h2>Join the local quote network.</h2>
            <p>Register your business for early access. We'll use this to understand your service area, categories, and quoting preferences before launch.</p>
          </div>
          <BusinessRegistrationForm />
        </section>

        {/* Customer quote request */}
        <section id="quote" className="section quote-section section-observe" data-section="quote">
          <div className="quote-copy reveal">
            <p className="eyebrow">Early access</p>
            <h2>Start with one clean request.</h2>
            <p>Designed for the MVP stage: simple for customers, structured enough for businesses, and ready to connect with your backend later.</p>
          </div>
          <CustomerQuoteForm />
        </section>

        {/* Contact */}
        <section id="contact" className="section contact-section section-observe" data-section="contact">
          <div className="section-kicker">Contact</div>
          <div className="contact-card reveal">
            <div>
              <h2>Contact Quoteramaa</h2>
              <p>For customer inquiries, business registration, partnerships, or privacy requests, contact us by email.</p>
            </div>
            <div className="contact-list">
              <p><span>Email</span><a href="mailto:info@quoteramaa.ca">info@quoteramaa.ca</a></p>
              <p><span>Location</span>Calgary, Alberta, Canada</p>
              <p><span>Business inquiries</span>Business registration, partnerships, and support</p>
            </div>
          </div>
        </section>
      </main>

      <footer className="apple-footer" aria-label="Quoteramaa footer">
        <div className="footer-inner">
          <p className="footer-note">
            Quoteramaa is a registered Alberta-based Canadian business building a local quote-request marketplace. We help customers organize quote requests and connect with relevant local businesses. Quoteramaa is not the service provider and does not guarantee pricing, availability, quality, or service outcomes.
          </p>

          <div className="footer-columns">
            <div className="footer-col">
              <h3>Quoteramaa</h3>
              <a href="#about">About</a>
              <a href="#how">How it works</a>
              <a href="#categories">Launch categories</a>
              <a href="#quote">Get quotes</a>
            </div>

            <div className="footer-col">
              <h3>For Customers</h3>
              <a href="#quote">Submit a request</a>
              <a href="#customers">Compare options</a>
              <a href="#how">Follow-up questions</a>
            </div>

            <div className="footer-col">
              <h3>For Businesses</h3>
              <a href="#register">Register business</a>
              <a href="#businesses">Business benefits</a>
              <a href="mailto:info@quoteramaa.ca?subject=Business%20registration%20inquiry">Partner inquiry</a>
            </div>

            <div className="footer-col">
              <h3>Company</h3>
              <span>QUOTERAMAA TECHNOLOGIES INC.</span>
              <span>Registered in Alberta</span>
              <span>Calgary, Alberta, Canada</span>
              <a href="mailto:info@quoteramaa.ca">info@quoteramaa.ca</a>
            </div>

            <div className="footer-col">
              <h3>Legal</h3>
              <a href={`${process.env.PUBLIC_URL}/docs/privacy-policy.pdf`} target="_blank" rel="noopener noreferrer">Privacy Policy</a>
              <a href={`${process.env.PUBLIC_URL}/docs/terms-of-use.pdf`} target="_blank" rel="noopener noreferrer">Terms of Use</a>
              <a href={`${process.env.PUBLIC_URL}/docs/marketplace-disclaimer.pdf`} target="_blank" rel="noopener noreferrer">Marketplace Disclaimer</a>
            </div>
          </div>

          <p className="footer-connect">
            More ways to connect: email <a href="mailto:info@quoteramaa.ca">info@quoteramaa.ca</a> for customer inquiries, business registration, partnerships, or privacy requests.
          </p>

          <div className="footer-bottom">
            <p>Copyright &copy; 2026 QUOTERAMAA TECHNOLOGIES INC. All rights reserved.</p>
            <div className="footer-mini-links">
              <a href={`${process.env.PUBLIC_URL}/docs/privacy-policy.pdf`} target="_blank" rel="noopener noreferrer">Privacy</a>
              <span>|</span>
              <a href={`${process.env.PUBLIC_URL}/docs/terms-of-use.pdf`} target="_blank" rel="noopener noreferrer">Terms</a>
              <span>|</span>
              <a href={`${process.env.PUBLIC_URL}/docs/marketplace-disclaimer.pdf`} target="_blank" rel="noopener noreferrer">Legal</a>
              <span>|</span>
              <span>Canada (English)</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
